import axiosInstance from '@/lib/api/axios-instance';
import { ENDPOINTS } from '@/lib/api/endpoints';
import type {
  IntegrationConfig,
  SyncLog,
  CreateIntegrationPayload,
  SyncResult,
  TestConnectionResult,
  HubspotCompany,
  HubspotContact,
  HubspotDeal,
  HubspotListParams,
  HubspotListResponse,
} from '../types/integration.types';
import type { HubspotSyncJobStatus } from '@/lib/hubspot-sync';

export const integrationService = {
  async getAll(): Promise<IntegrationConfig[]> {
    const { data } = await axiosInstance.get(ENDPOINTS.INTEGRATIONS);
    return data.data;
  },

  async getById(id: string): Promise<IntegrationConfig> {
    const { data } = await axiosInstance.get(ENDPOINTS.INTEGRATION_BY_ID(id));
    return data.data;
  },

  async create(payload: CreateIntegrationPayload): Promise<IntegrationConfig> {
    const { data } = await axiosInstance.post(ENDPOINTS.INTEGRATIONS, payload);
    return data.data;
  },

  async update(id: string, payload: Partial<CreateIntegrationPayload>): Promise<IntegrationConfig> {
    const { data } = await axiosInstance.put(ENDPOINTS.INTEGRATION_BY_ID(id), payload);
    return data.data;
  },

  async delete(id: string): Promise<void> {
    await axiosInstance.delete(ENDPOINTS.INTEGRATION_BY_ID(id));
  },

  async testConnection(id: string): Promise<TestConnectionResult> {
    const { data } = await axiosInstance.post(ENDPOINTS.INTEGRATION_TEST(id));
    return data.data;
  },

  async triggerSync(id: string, direction: 'inbound' | 'outbound'): Promise<SyncResult> {
    const { data } = await axiosInstance.post(
      `${ENDPOINTS.INTEGRATION_SYNC(id)}?direction=${direction}`
    );
    return data.data;
  },

  /** HubSpot: encola el «Importar» (202) y devuelve el trabajo a seguir. */
  async triggerHubspotSync(id: string): Promise<{ jobId: string; alreadyRunning: boolean }> {
    const { data } = await axiosInstance.post(
      `${ENDPOINTS.INTEGRATION_SYNC(id)}?direction=inbound`
    );
    return data.data;
  },

  async getHubspotSyncJob(id: string, jobId: string): Promise<HubspotSyncJobStatus> {
    const { data } = await axiosInstance.get(
      `/api/integrations/${id}/sync-jobs/${encodeURIComponent(jobId)}`
    );
    return data.data;
  },

  async getLogs(id: string, page = 1, limit = 20): Promise<{ data: SyncLog[]; pagination: any }> {
    const { data } = await axiosInstance.get(ENDPOINTS.INTEGRATION_LOGS(id), {
      params: { page, limit },
    });
    return { data: data.data, pagination: data.pagination };
  },

  async getAllLogs(page = 1, limit = 20): Promise<{ data: SyncLog[]; pagination: any }> {
    const { data } = await axiosInstance.get(ENDPOINTS.INTEGRATIONS_ALL_LOGS, {
      params: { page, limit },
    });
    return { data: data.data, pagination: data.pagination };
  },
};

function buildHubspotListUrl(
  integrationId: string,
  kind: 'companies' | 'contacts' | 'deals',
  params: HubspotListParams
): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.companyId) qs.set('companyId', params.companyId);
  if (params.stage) qs.set('stage', params.stage);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return `/api/integrations/${integrationId}/hubspot/${kind}${suffix}`;
}

export async function listHubspotCompanies(
  integrationId: string,
  params: HubspotListParams = {}
): Promise<HubspotListResponse<HubspotCompany>> {
  const { data } = await axiosInstance.get(buildHubspotListUrl(integrationId, 'companies', params));
  return data as HubspotListResponse<HubspotCompany>;
}

export async function listHubspotContacts(
  integrationId: string,
  params: HubspotListParams = {}
): Promise<HubspotListResponse<HubspotContact>> {
  const { data } = await axiosInstance.get(buildHubspotListUrl(integrationId, 'contacts', params));
  return data as HubspotListResponse<HubspotContact>;
}

export async function listHubspotDeals(
  integrationId: string,
  params: HubspotListParams = {}
): Promise<HubspotListResponse<HubspotDeal>> {
  const { data } = await axiosInstance.get(buildHubspotListUrl(integrationId, 'deals', params));
  return data as HubspotListResponse<HubspotDeal>;
}
